import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { ApiError } from './http/http-api';
import { queryKeyTargetsSession, queryKeys } from './query-keys';

/**
 * How long a cached answer stays fresh before a mount refetches it.
 *
 * A back office is read-mostly and several screens show the same list, so the number is
 * a compromise: brief enough that a colleague's change shows up on the next navigation,
 * long enough that moving between menu entries does not refetch everything.
 */
const QUERY_STALE_TIME_MS = 30_000;
const QUERY_GC_TIME_MS = 5 * 60_000;
const MAX_QUERY_RETRIES = 2;

/**
 * Which failures are worth retrying.
 *
 * A 4xx is the API's answer, not a hiccup: retrying a 403 or a 422 only delays the
 * message. A 5xx and a request that never arrived are worth two more attempts.
 */
const shouldRetry = (failureCount: number, error: unknown): boolean => {
  if (failureCount >= MAX_QUERY_RETRIES) return false;
  if (error instanceof ApiError) {
    if (error.code === 'REQUEST_TIMEOUT') return false;
    if (error.status >= 400 && error.status < 500) return false;
  }
  return true;
};

/**
 * The app's one query cache.
 *
 * The `QueryCache` hook exists for a single reason: a 401 anywhere means the session the
 * route guards read is gone. Invalidating it makes the next navigation redirect to the
 * login page instead of rendering a page whose data can never load.
 */
export const createQueryClient = (): QueryClient => {
  let sessionIdentity: string | null | undefined;
  const clearBusinessCache = () => {
    client.removeQueries({
      // Public legal documents are independent of the session and may still be loading.
      predicate: (query) =>
        !queryKeyTargetsSession(query.queryKey) &&
        !(query.queryKey[0] === 'legal' && query.queryKey[1] === 'documents'),
    });
    client.getMutationCache().clear();
  };
  const queryCache = new QueryCache({
    onSuccess: (data, query) => {
      if (!queryKeyTargetsSession(query.queryKey)) return;
      const session = data as {
        user: { id: string };
        session: { activeOrganizationId?: string | null };
      } | null;
      const identity = session
        ? JSON.stringify([
            session.user.id,
            session.session.activeOrganizationId ?? null,
          ])
        : null;
      if (identity === null || identity !== sessionIdentity) {
        clearBusinessCache();
      }
      sessionIdentity = identity;
    },
    onError: /** 非会话查询的 401 使当前会话缓存失效，不把 403 当作登出。 */ (
      error,
      query,
    ) => {
      if (!(error instanceof ApiError) || !error.requiresAuthentication) return;
      // The session query itself failing 401 is the expected "not signed in" path.
      if (queryKeyTargetsSession(query.queryKey)) return;
      clearBusinessCache();
      void client.invalidateQueries({ queryKey: queryKeys.session });
    },
  });
  const mutationCache = new MutationCache({
    onError: (error) => {
      if (!(error instanceof ApiError) || !error.requiresAuthentication) return;
      clearBusinessCache();
      void client.invalidateQueries({ queryKey: queryKeys.session });
    },
  });
  const client = new QueryClient({
    queryCache,
    mutationCache,
    defaultOptions: {
      queries: {
        staleTime: QUERY_STALE_TIME_MS,
        gcTime: QUERY_GC_TIME_MS,
        retry: shouldRetry,
        // Refetching an admin table because the operator switched to a spreadsheet and
        // back is noise, not freshness.
        refetchOnWindowFocus: false,
      },
      mutations: { retry: false },
    },
  });
  return client;
};
